/**
 * useMenuPlugins.ts - React Hook for Menu Plugin Integration
 * 
 * Provides reactive access to plugin-registered menu items
 * 
 * CHANGE HISTORY:
 * - 251115_1845 [current] danielbeer: Created useMenuPlugins hook
 */

import { useState, useEffect, useMemo } from 'react';
import { getMenuPluginManager } from './MenuPluginManager';
import type { MenuItem } from './types';

export interface UseMenuPluginsOptions {
  menuId: 'side' | 'top' | 'context';
  enabled?: boolean;
}

export function useMenuPlugins(options: UseMenuPluginsOptions) {
  const { menuId, enabled = true } = options;
  const [pluginItems, setPluginItems] = useState<MenuItem[]>([]);
  
  // Get plugin manager instance
  const pluginManager = useMemo(() => {
    return getMenuPluginManager();
  }, []);
  
  // Subscribe to plugin item changes
  useEffect(() => {
    if (!enabled) {
      setPluginItems([]);
      return;
    }
    
    // Initial load
    const updateItems = () => {
      const items = pluginManager.getMenuItems(menuId);
      setPluginItems(items);
    };
    
    updateItems();
    
    // Subscribe to changes
    const unsubscribe = pluginManager.subscribe(updateItems);
    
    return unsubscribe;
  }, [pluginManager, menuId, enabled]);
  
  // Get statistics
  const stats = useMemo(() => {
    return pluginManager.getStats();
  }, [pluginManager, pluginItems]); // Re-compute when items change
  
  return {
    pluginItems,
    stats,
    pluginManager
  };
}
